
import { useState, useEffect, useCallback, useRef } from "react";
import { Movie, PendingAction } from "@/types/movie";
import { debugLog, isDebugMovie } from "@/utils/debugLogger";
import { UNDO_DURATION } from "@/context/movie/movieUtils";

export const useUndoCardLogic = (
  movie: Movie,
  action: PendingAction,
  onUndo: (actionId: string) => void
) => {
  const [progress, setProgress] = useState<number>(100);
  const [isExpiring, setIsExpiring] = useState(false);
  const isDebug = isDebugMovie(movie);
  
  // Use refs to track initialization state and avoid duplicate logs
  const animationStarted = useRef(false);
  const hasLogged = useRef(false);
  
  // Log only once per actionId
  useEffect(() => {
    if (!hasLogged.current) {
      debugLog('important', `UndoCard: Initializing for movie: ${movie.title}, action: ${action.id}`);
      hasLogged.current = true;
    }
    
    return () => {
      if (hasLogged.current) {
        debugLog('important', `UndoCard: Cleanup for movie: ${movie.title}, action: ${action.id}`);
        hasLogged.current = false;
      }
    };
  }, [action.id, movie.title]);
  
  // Handle animation setup with requestAnimationFrame
  useEffect(() => {
    if (!animationStarted.current) {
      const frameId = requestAnimationFrame(() => {
        setProgress(0);
        animationStarted.current = true;
      });
      
      // Calculate remaining time and set expiring state
      const elapsedTime = Date.now() - action.timestamp;
      const remainingTime = UNDO_DURATION - elapsedTime;
      const expiringTimer = setTimeout(() => {
        setIsExpiring(true);
      }, Math.max(0, remainingTime - 300)); // 300ms before expiration

      return () => {
        cancelAnimationFrame(frameId);
        clearTimeout(expiringTimer);
      };
    }
  }, [action.id, action.timestamp]);

  // Handle undo action with event stopping
  const handleUndo = (e: React.MouseEvent) => {
    e.stopPropagation();
    debugLog('action', `UndoCard: Manual undo clicked for action: ${action.id}, movie: ${movie.title}`);
    onUndo(action.id);
  };

  // Get descriptive text for the action
  const getActionText = useCallback(() => {
    if (action.type === 'favorite') {
      return action.movieData.favorite ? 'Removed from gems' : 'Added to gems';
    } else if (action.type === 'planToWatch') {
      return 'Removed from watchlist';
    }
    return 'Removed';
  }, [action.type, action.movieData]);

  return {
    progress,
    isExpiring,
    getActionText,
    handleUndo
  };
};
